import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import { selectActiveGame, selectActivePokemon, selectOptions } from '../ActiveGame/selectors';
import { putActivePokemon } from '../GameStart/actions';
import { ATTACH, BENCH, EVOLVE, handCardAction, MAKE_ACTIVE, TRAINER } from './handCardAction';
import TrainerMenu from '../Trainer/TrainerMenu';
import { Button, Muted } from '../Controls';
import { selectAttachFlowing, selectBenchFlowing, selectEvolveFlowing } from '../Pokemon/selectors';
import { selectPuttingActive } from '../GameStart/selectors';

/** One action for the card: a button, greyed out with the server's reason when it can't be done now. */
const Choice = ({ reason, children }) => (
  <>
    {children}
    {reason && <Muted>{reason}</Muted>}
  </>
);

Choice.propTypes = {
  reason: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const BUTTON_LABELS = { [BENCH]: 'Put on bench', [ATTACH]: 'Attach to a Pokémon', [EVOLVE]: 'Evolve' };

/**
 * What can be done with one card from your hand right now: handCardAction decides it (shared with dragging), and
 * whether it's allowed comes from the server's options, so the menu never works out the rules itself.
 */
const HandMenu = ({ card, userId }) => {
  const dispatch = useDispatch();
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const attachFlowing = useSelector(selectAttachFlowing);
  const benchFlowing = useSelector(selectBenchFlowing);
  const evolveFlowing = useSelector(selectEvolveFlowing);
  const game = useSelector(selectActiveGame);
  const options = useSelector(selectOptions);
  const puttingActive = useSelector(selectPuttingActive);

  const action = handCardAction(card, { settingUp: !game.get('active_player_id'), hasActive: !!activePokemon, options });
  const flowing = { [BENCH]: benchFlowing, [ATTACH]: attachFlowing, [EVOLVE]: evolveFlowing };

  if (action.kind === MAKE_ACTIVE) {
    return (
      <Button $primary onClick={() => dispatch(putActivePokemon(card.get('order')))} disabled={puttingActive}>
        Make active
      </Button>
    );
  }

  if (action.kind === TRAINER) {
    return <TrainerMenu card={card} />;
  }

  return (
    <Choice reason={action.reason}>
      <Button $primary onClick={() => dispatch(action.start())} disabled={!!action.reason || flowing[action.kind]}>
        {BUTTON_LABELS[action.kind]}
      </Button>
    </Choice>
  );
};

HandMenu.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
  userId: PropTypes.number.isRequired,
};

export default HandMenu;
